export const name = 'systemStatisticsService';
export default function systemStatisticsService(rnHttp, $q, $log) {
  //noinspection BadExpressionStatementJS
  'ngInject';

  const SYSTEM_STATISTICS_SERVICE_NAME = 'system_statistics';

  var service = {
    getAllDeviceStats: getAllDeviceStats,
    getDeviceStats: getDeviceStats,
    processExtendedInformation: processExtendedInformation
  };

  return service;

  ////////////////

  // The caller should pass in the existing device data, and we will overlay it with the
  // latest stats using extend and return a promise to the updated data.
  function getAllDeviceStats(deviceData) {
    let deviceIds = _.pluck(deviceData, 'deviceId');
    let deviceIdChunks = [];
    while (deviceIds.length > 0) {
      deviceIdChunks.push(deviceIds.splice(0, 75));
    }

    let promises = [];
    deviceIdChunks.forEach(deviceIdChunk => {
      let config = {
        version: 2,
        params: {
          device: deviceIdChunk
        }
      };

      let promise = rnHttp.get(SYSTEM_STATISTICS_SERVICE_NAME, 'devices/latest', config)
        .then(function (response) {
          let data = response.data;
          data.deviceStatistics.forEach(function (value) {
            let device = _.find(deviceData, { deviceId: value.deviceId });
            if (device) {
              processExtendedInformation(device.type, value);
              angular.extend(device, value);
            }
          });

          return deviceData;
        })
        .catch(function (response) {
          return $q.reject(_.get(response, 'data.smsError', response));
        });
      promises.push(promise);
    });

    return $q.all(promises)
      .then(deviceDataArrays => {
        return _.flatten(deviceDataArrays);
      })
      .catch(err => {
        return $q.reject(err);
      });
  }

  function getDeviceStats(deviceId, deviceData) {
    var deferred = $q.defer();
    rnHttp.get(SYSTEM_STATISTICS_SERVICE_NAME, 'devices/' + deviceId + '/latest', {version: 2})
      .then(function (response) {
        let data = response.data;
        let device = _.find(deviceData, { deviceId: data.deviceId });
        if (device) {
          processExtendedInformation(device.type, data);
          angular.extend(device, data);
        }

        deferred.resolve(deviceData);
      })
      .catch(function (response) {
        deferred.reject(_.get(response, 'data.smsError', response));
      });

    return deferred.promise;
  }

  /**
   * If there is any extended information in the stats record, process it into a form that's easier to deal with
   * in the markup.
   */
  function processExtendedInformation(deviceType, device) {
    if (device) {
      if (!device.data) {
        device.data = {};
      }
      let deviceData = device.data;

      deviceData.exists = {
        primaryInks: false,
        intermediateInks: false,
        loadedSubstrates: false,
        printingJobs: false,
        queuedJobs: false,
        jobHistory: false,
        maintenance: false,
        notifications: false,
        jobFiltersBrief: false,
        jobFiltersVerbose: false,
        lastUpdate: false,
        colorControl: false
      };

      deviceData.inks = null;
      deviceData.substrates = null;
      deviceData.jobs = null;
      deviceData.maintenance = null;
      deviceData.notifications = null;
      deviceData.jobFilters = null;
      deviceData.miscInfo = {};
      deviceData.colorControl = null;

      if (deviceData) {
        try {
          // Fixup the deviceData to make sure it is in the correct buckets (generalData, inkData, substrateData, jobData), even if it was posted with a v1 API
          fixupDeviceData(deviceType, deviceData);

          // Set lastUpdate to the timestamp of when the record was updated or created. It may be overwritten later from extended information
          if (device.updatedTime) {
            deviceData.exists.lastUpdate = true;
            deviceData.lastUpdate = device.updatedTime;
          }
          else if (device.createdTime) {
            deviceData.exists.lastUpdate = true;
            deviceData.lastUpdate = device.createdTime;
          }

          // Perform device specific parsing of the incoming data into a common format that the UI will understand regardless of what device it is
          switch(deviceType) {
            case "DesignjetPrinter":
            case "LatexPrinter":
            case "TextilePrinter":
            case "PageWideXLPrinter":
              processLFData(deviceData);
              break;
            case "ThreeDPrinter":
            case "ThreeDController":
              process3DData(deviceData);
              break;
            case "ProductionCenter":
              processProductionCenterData(deviceData);
              break;
            case "RIP":
              processColorControlData(deviceData);
              break;
            default:
              break;
          }
        }
        catch (e) {
          $log.debug("Error parsing extended info: " + e);
        }
      }
    }
  }

  function fixupDeviceData(deviceType, deviceData) {
    // Account for devices that posted their data with the v1 API where everything was in extendedInformation (and then renamed to generalData).
    // Make them look as if they posted with v2 where there are different buckets (generalData, inkData, substrateData, jobData)
    switch(deviceType) {
      case "DesignjetPrinter":
      case "LatexPrinter":
      case "TextilePrinter":
      case "PageWideXLPrinter":
        if (deviceData.generalData && deviceData.generalData.printer_data && deviceData.generalData.printer_data.data) {
          let data = deviceData.generalData.printer_data.data;
          // Ink
          if (data.ink_info) {
            if (!deviceData.inkData) {
              deviceData.inkData = {};
            }
            deviceData.inkData.ink_info = data.ink_info;
            delete data.ink_info;
          }
          if (data.intermediate_tanks) {
            if (!deviceData.inkData) {
              deviceData.inkData = {};
            }
            deviceData.inkData.intermediate_tanks = data.intermediate_tanks;
            delete data.intermediate_tanks;
          }

          // Substrate
          if (data.substrates) {
            if (!deviceData.substrateData) {
              deviceData.substrateData = {};
            }
            deviceData.substrateData.substrates = data.substrates;
            delete data.substrates;
          }

          // Job
          if (data.job) {
            if (!deviceData.jobData) {
              deviceData.jobData = {};
            }
            deviceData.jobData.job = data.job;
            delete data.job;
          }
        }
        break;
      case "ProductionCenter":
        // Move the PC job filters out of generalData and into jobData
        if (deviceData.generalData && deviceData.generalData.job) {
          if (!deviceData.jobData) {
            deviceData.jobData = {};
            deviceData.jobData.job = deviceData.generalData.job;
            delete deviceData.generalData.job;
          }
        }
        break;
      case "RIP":
        // Everything can remain in generalData
        break;
      default:
        break;
    }
  }

  function processLFData(deviceData) {
    try {
      if (deviceData.inkData) {
        let data = deviceData.inkData;

        // Inks
        if (data.ink_info && (data.ink_info.length > 0)) {
          if (!deviceData.inks) {
            deviceData.inks = {};
          }
          deviceData.inks.primaryInkLevels = [];
          // Look for known ink values and put them in the preferred order.
          let labels = ["M", "LM", "LC", "C", "OP", "Y", "CR", "CG", "CB", "G", "K", "MK", "PK", "W", "OC", "E"];
          for (let i = 0; i < labels.length; i++) {
            for (let y = 0; y < data.ink_info.length; y++) {
              let consumable = data.ink_info[y].consumable_label_code;
              if (consumable && (consumable === labels[i])) {
                let percentFull = 0;
                let currentLevel = parseFloat(data.ink_info[y].Level);
                let maxCapacity = parseFloat(data.ink_info[y].max_capacity);
                if (currentLevel > 0 && maxCapacity > 0) {
                  if(currentLevel > maxCapacity) {
                    currentLevel = 0;
                    data.ink_info[y].status_id = 'error';
                  }
                  else {
                    percentFull = Math.round(currentLevel / maxCapacity * 100);
                  }
                }
                let level = {
                  id: "ink_" + i,
                  label: labels[i],
                  productName: data.ink_info[y].product_name,
                  productNumber: data.ink_info[y].product_number,
                  level: currentLevel,
                  statusId: data.ink_info[y].status_id,
                  value: percentFull.toString(),
                  height: percentFull.toString() + "%",
                  pad: (100 - percentFull) + "%",
                  statusKey: "devices.devices.Latex_Cartridge_State_" + data.ink_info[y].status_id,
                  measuredQuantityState: data.ink_info[y].measured_quantity_state,
                  expirationDate: data.ink_info[y].expiration_date
                };
                deviceData.inks.primaryInkLevels.push(level);
                break;
              }
            }
          }
          deviceData.exists.primaryInks = true;
        }

        // Intermediate inks
        if (data.intermediate_tanks && (data.intermediate_tanks.length > 0)) {
          if (!deviceData.inks) {
            deviceData.inks = {};
          }
          deviceData.inks.intermediateInkLevels = [];
          // Look for known ink values and put them in the preferred order.
          let labels = ["M", "LM", "LC", "C", "OP", "Y", "K", "W"];
          for (let i = 0; i < labels.length; i++) {
            for (let y = 0; y < data.intermediate_tanks.length; y++) {
              let consumable = data.intermediate_tanks[y].consumable_label_code;
              if (consumable && (consumable === labels[i])) {
                let percentFull = 0;
                if (data.intermediate_tanks[y].Level > 0 && data.intermediate_tanks[y].max_capacity > 0) {
                  percentFull = Math.round(data.intermediate_tanks[y].Level / data.intermediate_tanks[y].max_capacity * 100);
                }
                let level = {
                  id: "ink_" + i,
                  label: labels[i],
                  productName: data.intermediate_tanks[y].product_name,
                  productNumber: data.intermediate_tanks[y].product_number,
                  level: data.intermediate_tanks[y].Level,
                  statusId: data.intermediate_tanks[y].status_id,
                  value: percentFull.toString(),
                  height: percentFull.toString() + "%",
                  pad: (100 - percentFull) + "%",
                  statusKey: "devices.devices.Latex_Cartridge_State_" + data.ink_info[y].status_id,
                  measuredQuantityState: data.intermediate_tanks[y].measured_quantity_state,
                  expirationDate: data.intermediate_tanks[y].expiration_date
                };
                deviceData.inks.intermediateInkLevels.push(level);
                break;
              }
            }
          }
          deviceData.exists.intermediateInks = true;
        }
      }

      if (deviceData.substrateData) {
        let data = deviceData.substrateData;

        // Substrate
        if (data.substrates && (data.substrates.length > 0)) {
          if (!deviceData.substrates) {
            deviceData.substrates = {};
          }
          deviceData.substrates.loadedSubstrates = [];
          for (let i = 0; i < data.substrates.length; i++) {
            let widthmm = data.substrates[i].media_width * 1000;  // Convert to mm
            let mediaWidth = Math.round(widthmm * 100) / 100;     // Rounded, 2 decimal places

            // media_counter will eventually be deprecated and replaced with remaining_length or substrate_counter. Only one should exist
            let remainingLength = null;
            let substrateCounter = null;
            let mediaCounter = null;
            if (data.substrates[i].remaining_length && data.substrates[i].remaining_length !== "Unknown") {
              remainingLength = data.substrates[i].remaining_length;        // Leave in meters
              remainingLength = Math.round(remainingLength * 100) / 100;    // Rounded, 2 decimal places
            }
            else if (data.substrates[i].substrate_counter && data.substrates[i].substrate_counter !== "Unknown") {
              substrateCounter = data.substrates[i].substrate_counter;        // Leave in meters
              substrateCounter = Math.round(substrateCounter * 100) / 100;    // Rounded, 2 decimal places
            }
            else if (data.substrates[i].media_counter && (data.substrates[i].media_counter.value !== undefined) && (data.substrates[i].media_counter.value !== "Unknown")) {
              mediaCounter = data.substrates[i].media_counter.value;  // Leave in meters
              mediaCounter = Math.round(mediaCounter * 100) / 100;    // Rounded, 2 decimal places
            }


            let substrate = {
              mediaName: data.substrates[i].media_name,
              mediaFormat: data.substrates[i].media_format,
              mediaWidth: mediaWidth,
              trayState: data.substrates[i].tray_state,
              measuredQuantity: data.substrates[i].measured_quantity,
              mediaCounter: mediaCounter,
              remainingLength: remainingLength,
              substrateCounter: substrateCounter
            };
            if (substrate.mediaName != null) {
              deviceData.substrates.loadedSubstrates.push(substrate);
            }
          }
          if (deviceData.substrates.loadedSubstrates.length > 0) {
            deviceData.exists.loadedSubstrates = true;   // Only indicate that it exists if we have a name for it. We have seen that if media is "out" all of the fields will be null
          }
        }
      }

      if (deviceData.jobData) {
        let data = deviceData.jobData;

        // Printing Jobs
        if (data.job && data.job.printing_jobs) {
          if (!deviceData.jobs) {
            deviceData.jobs = {};
          }
          deviceData.jobs.printingJobs = [];
          data.job.printing_jobs.forEach(function(item) {
            let jobState = "unknown";
            if (item.job_state) {
              // The job_state will be translated
              jobState = item.job_state.toLowerCase().replace(/[^A-Z0-9]/ig, "");
            }
            item.jobStateKey = "devices.devices.Latex_Job_Status_" + jobState;
            deviceData.jobs.printingJobs.push(item);
            deviceData.exists.printingJobs = true;
          });
        }

        // Queued Jobs
        if (data.job && data.job.future) {
          if (!deviceData.jobs) {
            deviceData.jobs = {};
          }
          deviceData.jobs.queuedJobs = [];
          data.job.future.forEach(function(item) {
            let jobState = "unknown";
            if (item.job_state) {
              // The job_state will be translated
              jobState = item.job_state.toLowerCase().replace(/[^A-Z0-9]/ig, "");
            }
            item.jobStateKey = "devices.devices.Latex_Job_Status_" + jobState;
            deviceData.jobs.queuedJobs.push(item);
            deviceData.exists.queuedJobs = true;
          });
        }

        // Job History
        if (data.job && data.job.history) {
          if (!deviceData.jobs) {
            deviceData.jobs = {};
          }
          deviceData.jobs.jobHistory = [];
          data.job.history.forEach(function(item) {
            let jobState = "unknown";
            if (item.end_state) {
              // The end_state will be translated
              jobState = item.end_state.toLowerCase().replace(/[^A-Z0-9]/ig, "");
            }
            item.jobStateKey = "devices.devices.Latex_Job_Status_" + jobState;
            deviceData.jobs.jobHistory.push(item);
            deviceData.exists.jobHistory = true;
          });
        }
      }

      if (deviceData.generalData && deviceData.generalData.printer_data && deviceData.generalData.printer_data.data) {
        let data = deviceData.generalData.printer_data.data;

        // Maintenance
        if (data.monocassette_info) {
          deviceData.maintenance = {
            productNumber: data.monocassette_info.product_number,
            maxCapacity: data.monocassette_info.max_capacity,
            level: data.monocassette_info.level,
            consumableState: data.monocassette_info.consumable_state,
          };
          deviceData.exists.maintenance = true;
        }

        // Notifications
        if (data.notifications !== undefined) {
          data.notifications.list.forEach(function(item) {
            if (item.severity) {
              item.severityKey = "devices.devices.Device_Notifications_" + item.severity.toLowerCase();
            }
          });
          deviceData.notifications = {
            list: data.notifications.list
          };
          deviceData.exists.notifications = true;

          // Device Specific Detailed Status
          if (data.notifications.front_panel_message) {
            // This will be shown 'as is' without localization on the details page
            deviceData.miscInfo.deviceSpecificDetailedStatusMsg = data.notifications.front_panel_message;
            deviceData.exists.deviceSpecificDetailedStatusMsg = true;
          }
        }

        // Device Specific Status Key
        if (data.printer_info && data.printer_info.status) {
          // This will be used to lookup the basic device status instead of the generic status string
          deviceData.miscInfo.deviceSpecificStatusKey = "devices.devices.Latex_Status_" + data.printer_info.status.toLowerCase();
          deviceData.exists.deviceSpecificStatusKey = true;
        }

        // Device specific product name (model string)
        if (data.printer_info && (data.printer_info.printer_model !== undefined)) {
          deviceData.miscInfo.deviceSpecificProductName = data.printer_info.printer_model;
          deviceData.exists.deviceSpecificProductName = true;
        }

        // Firmware Version
        if (data.printer_info && (data.printer_info.printer_firmware_version !== undefined)) {
          deviceData.miscInfo.firmwareVersion = data.printer_info.printer_firmware_version;
          deviceData.exists.firmwareVersion = true;
        }

        // Last Update
        if ((data.printer_info) && (data.printer_info.last_update_at)) {
          // Set to the value received from the device
          deviceData.lastUpdate = data.printer_info.last_update_at;
          deviceData.exists.lastUpdate = true;
        }
      }
    }
    catch (e) {
      $log.debug("Error processing large format info: " + e);
    }
  }

  function process3DData(deviceData) {
    try {
      if (deviceData.generalData && deviceData.generalData.printer_data && deviceData.generalData.printer_data.data) {
        let data = deviceData.generalData.printer_data.data;

        // Notifications
        if (data.notifications !== undefined) {
          data.notifications.list.forEach(function(item) {
            if (item.severity) {
              item.severityKey = "devices.devices.Device_Notifications_" + item.severity.toLowerCase();
            }
          });
          deviceData.notifications = {
            list: data.notifications.list
          };
          deviceData.exists.notifications = true;
        }

        // Firmware Version
        if (data.printer_info && (data.printer_info.printer_firmware_version !== undefined)) {
          deviceData.miscInfo.firmwareVersion = data.printer_info.printer_firmware_version;
          deviceData.exists.firmwareVersion = true;
        }
      }
    }
    catch (e) {
      $log.debug("Error processing 3D info: " + e);
    }
  }

  function processProductionCenterData(deviceData) {
    try {
      if (deviceData.jobData) {
        if (deviceData.jobData.job && deviceData.jobData.job.filters) {
          if (!deviceData.jobFilters) {
            deviceData.jobFilters = {};
          }
          // Job Filters
          // The way the filters should be displayed is that the title should be displayed 'as is' if it’s non-null, else use the id as the loc key.
          // The id comes to us like "ACTIVE_JOBS" so we need to prepend the devices.devices.PC_Filter_ to it so we can look it up in our catalog.
          if (deviceData.jobData.job.filters.brief) {
            deviceData.jobFilters.jobFiltersBrief = [];
            deviceData.jobData.job.filters.brief.forEach(function(item) {
              let filter = {
                id: item.id ? item.id : null,
                titleKey: item.id ? "devices.devices.PC_Filter_" + item.id : null,
                title: item.title ? item.title : null,
                count: item.count
              };
              deviceData.jobFilters.jobFiltersBrief.push(filter);
              deviceData.exists.jobFiltersBrief = true;
            });
          }

          if (deviceData.jobData.job.filters.verbose) {
            deviceData.jobFilters.jobFiltersVerbose = [];
            deviceData.jobData.job.filters.verbose.forEach(function(item) {
              let filter = {
                id: item.id ? item.id : null,
                titleKey: item.id ? "devices.devices.PC_Filter_" + item.id : null,
                title: item.title ? item.title : null,
                count: item.count
              };
              deviceData.jobFilters.jobFiltersVerbose.push(filter);
              deviceData.exists.jobFiltersVerbose = true;
            });
          }
        }
      }
    }
    catch (e) {
      $log.debug("Error processing production center info: " + e);
    }
  }

  function processColorControlData(deviceData) {
    try {
      deviceData.colorControl = {};
      if (deviceData.colorControl.lastScan) {
        deviceData.generalData.lastScan = deviceData.generalData.lastScan;
      }
      if (deviceData.generalData.press) {
        deviceData.colorControl.press = deviceData.generalData.press;
      }
      if (deviceData.generalData.target) {
        deviceData.colorControl.target = deviceData.generalData.target;
      }
      if (deviceData.generalData.substrates) {
        deviceData.colorControl.substrates = deviceData.generalData.substrates;
      }
      if (deviceData.generalData.jobName) {
        deviceData.colorControl.jobName = deviceData.generalData.jobName;
      }
      if (deviceData.generalData.result) {
        deviceData.colorControl.result = deviceData.generalData.result;
      }
      if (deviceData.generalData.color) {
        switch(deviceData.generalData.color) {
          case 'G' :
            deviceData.colorControl.color = '#509137';           // Matches the $hp-status-green
            break;
          case 'Y' :
            deviceData.colorControl.color = '#F0AA3C';           // Matches the $hp-status-yellow
            break;
          case 'R' :
            deviceData.colorControl.color = '#F04B37';           // Matches the $hp-status-red
            break;
          default :
            deviceData.colorControl.color = '#509137';           // Matches the $hp-status-green
            break;
        }
      }
      if (deviceData.generalData.fileType) {
        deviceData.colorControl.fileType = deviceData.generalData.fileType;
      }
      if (deviceData.generalData.custom1) {
        deviceData.colorControl.custom1 = deviceData.generalData.custom1;
      }
      if (deviceData.generalData.custom2) {
        deviceData.colorControl.custom2 = deviceData.generalData.custom2;
      }
      if (deviceData.generalData.custom3) {
        deviceData.colorControl.custom3 = deviceData.generalData.custom3;
      }
    }
    catch (e) {
      $log.debug("Error processing color control info: " + e);
    }
  }
}



// WEBPACK FOOTER //
// ../~/@printos/rhino/src/components/device/system-statistics.service.js